import React, { useState, ReactNode, useEffect } from "react";
import {
  DeepDiveTrackingContext,
  DeepDiveTracking,
} from "./DeepDiveTrackingContext";

interface DeepDiveTrackingProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = "deep-dive-tracking";

export const DeepDiveTrackingProvider: React.FC<
  DeepDiveTrackingProviderProps
> = ({ children }) => {
  const [deepDiveTrackings, setDeepDiveTrackings] = useState<
    DeepDiveTracking[]
  >(loadInitialStateFromLocalStorage);

  // Load from localStorage on mount
  function loadInitialStateFromLocalStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert updatedAt strings back to Date objects
        const trackings = parsed.map(
          (tracking: DeepDiveTracking & { updatedAt: string }) => ({
            ...tracking,
            updatedAt: new Date(tracking.updatedAt),
          }),
        );
        return trackings;
      }
    } catch (error) {
      console.error(
        "Failed to load deep dive tracking from localStorage:",
        error,
      );
    }
    return [];
  }

  // Save to localStorage whenever deepDiveTrackings changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(deepDiveTrackings));
    } catch (error) {
      console.error(
        "Failed to save deep dive tracking to localStorage:",
        error,
      );
    }
  }, [deepDiveTrackings]);

  const updateDeepDiveStatus = (tracking: DeepDiveTracking) => {
    setDeepDiveTrackings((prev) => {
      // Remove any existing entry for this identifier to avoid duplicates
      const filtered = prev.filter((t) => {
        if (tracking.type === "event" && t.type === "event") {
          return t.eventId !== tracking.eventId;
        } else if (tracking.type === "topic" && t.type === "topic") {
          return t.topic !== tracking.topic;
        }
        return true; // Keep entries of different types
      });
      return [...filtered, tracking];
    });
  };

  const isEventDeepDiveCompleted = (eventId: string): boolean => {
    return deepDiveTrackings.some(
      (t) => t.type === "event" && t.eventId === eventId && t.status === "done",
    );
  };

  const isEventDeepDiveFailed = (eventId: string): boolean => {
    return deepDiveTrackings.some(
      (t) =>
        t.type === "event" && t.eventId === eventId && t.status === "failed",
    );
  };

  const getEventDeepDiveTracking = (
    eventId: string,
  ): DeepDiveTracking | undefined => {
    return deepDiveTrackings.find(
      (t) => t.type === "event" && t.eventId === eventId,
    );
  };

  const clearDeepDiveTracking = () => {
    setDeepDiveTrackings([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const isTopicDeepDiveCompleted = (topic: string): boolean => {
    return deepDiveTrackings.some(
      (t) => t.type === "topic" && t.topic === topic && t.status === "done",
    );
  };

  const isTopicDeepDiveFailed = (topic: string): boolean => {
    return deepDiveTrackings.some(
      (t) => t.type === "topic" && t.topic === topic && t.status === "failed",
    );
  };

  const getTopicDeepDiveTracking = (
    topic: string,
  ): DeepDiveTracking | undefined => {
    return deepDiveTrackings.find(
      (t) => t.type === "topic" && t.topic === topic,
    );
  };

  const getPendingEventDeepDives = (): DeepDiveTracking[] => {
    return deepDiveTrackings.filter(
      (t) => t.type === "event" && t.status === "pending",
    );
  };

  const getPendingTopicDeepDives = (): DeepDiveTracking[] => {
    return deepDiveTrackings.filter(
      (t) => t.type === "topic" && t.status === "pending",
    );
  };

  const removeDeepDiveTracking = (type: "event" | "topic", id: string) => {
    setDeepDiveTrackings((prev) => {
      return prev.filter((t) => {
        if (type === "event" && t.type === "event") {
          return t.eventId !== id;
        } else if (type === "topic" && t.type === "topic") {
          return t.topic !== id;
        }
        return true; // Keep entries of different types
      });
    });
  };

  return (
    <DeepDiveTrackingContext.Provider
      value={{
        deepDiveTrackings,
        updateDeepDiveStatus,
        isEventDeepDiveCompleted,
        isEventDeepDiveFailed,
        getEventDeepDiveTracking,
        clearDeepDiveTracking,
        isTopicDeepDiveCompleted,
        isTopicDeepDiveFailed,
        getTopicDeepDiveTracking,
        getPendingEventDeepDives,
        getPendingTopicDeepDives,
        removeDeepDiveTracking,
      }}
    >
      {children}
    </DeepDiveTrackingContext.Provider>
  );
};
